"use client";
import Icon from "@/components/Icon";
import { useNotification } from "@/components/NotificationContext";
import { useEffect, useState } from "react";
import { ExtractFreeControllerService } from "@/api";
import { useTranslation } from "react-i18next";
import { useSearchExtract } from "./ExtractSearchContext";

interface VideoExtractorProps {
  locale: string;
}

export const SearchVideoExtract = ({ locale }: VideoExtractorProps) => {
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const [url, setUrl] = useState("");
  const [showClearButton, setShowClearButton] = useState(false);
  const { setInfo } = useSearchExtract();
  const [isLoading, setIsLoading] = useState(false);

  const handleExtractVideo = async () => {
    if (!url) {
      showNotification(t("ERR_URL_EMPTY"), { type: "error" });
      return;
    }
    setIsLoading(true);
    try {
      const { data: result } =
        await ExtractFreeControllerService.extractSearchVideoUsingPost({
          body: { text: url, locale: locale },
        });
      if (!result || result.code != "OK") {
        showNotification(t(result?.code ?? ""), { type: "error" });
      } else {
        setInfo(result.data);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  // 清除输入框
  const clearInput = () => {
    setUrl("");
    setShowClearButton(false);
  };

  // 处理输入框变化
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUrl(value);
    setShowClearButton(value.length > 0);
  };

  const handleClipboard = async () => {
    try {
      const content = await navigator.clipboard.readText();
      setUrl(content);
      setShowClearButton(true);
    } catch (err) {}
  };

  return (
    <div className="mb-6">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-grow">
          <input
            type="text"
            id="douyin-url"
            value={url}
            className="w-full border border-gray-200 rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={"请输入抖音搜索链接"}
            onChange={handleUrlChange}
          />
          {showClearButton && (
            <button
              id="clear-input"
              className="cursor-pointer absolute right-3 top-1/2  transform -translate-y-1/2  transition-colors duration-200"
              onClick={clearInput}
            >
              <Icon
                name="icon-qingchu4"
                className="w-6 h-6= mr-1 text-gray-400 hover:text-gray-800"
              />
            </button>
          )}
        </div>
        <button
          id="paste-btn"
          className="cursor-pointer px-6 py-3 bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-800 active:bg-gray-200 transition-all duration-200 flex items-center justify-center whitespace-nowrap"
          onClick={handleClipboard}
        >
          <Icon name="icon-niantie" className="w-5 h-5 mr-2 " /> {t("paste")}
        </button>
        <button
          id="extract-btn"
          className={`cursor-pointer px-6 py-3 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white rounded-lg hover:opacity-90 transition-all duration-200 flex items-center justify-center whitespace-nowrap ${
            isLoading ? "cursor-not-allowed" : ""
          }`}
          onClick={handleExtractVideo}
          disabled={isLoading} // 禁用按钮
        >
          {isLoading ? (
            // 加载状态：显示旋转图标
            <>
              <Icon
                name="icon-loading"
                className="w-5 h-5 mr-2 animate-spin text-white"
              />
              {t("extracting_video")}
            </>
          ) : (
            <>
              <Icon name="icon-chuangjian" className="w-5 h-5 mr-2" />
              解析视频
            </>
          )}
        </button>
      </div>
    </div>
  );
};
